import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import {
  ComponentType,
  ExtraStyleType,
  FieldCustom,
} from 'components/Common/Formik/FieldCustom/FieldCustom';

import styles from './DateFieldCustom.module.scss';
import fieldStyles from '../FieldCustom/FieldCustom.module.scss';

/**
 * @deprecated Use FormikDateInputsRow instead (new UI).
 */
export default function DateFieldCustom({
  disabled,
  extraStyle,
  label,
  name,
  required,
  autoComplete,
}: {
  disabled?: boolean;
  extraStyle?: ExtraStyleType;
  label?: string;
  name: string;
  required?: boolean;
  autoComplete?: AutoCompleteType;
}): JSX.Element {
  extraStyle = {
    ...extraStyle,
    fieldContainer: classNames(
      extraStyle?.fieldContainer,
      styles.fieldContainer
    ),
    field: classNames(extraStyle?.field, styles.field),
  };

  return (
    <FieldCustom
      component={CustomizedDateField}
      disabled={disabled}
      {...(extraStyle && { extraStyle: extraStyle })}
      label={label}
      name={name}
      required={required}
      type="text"
      autoComplete={autoComplete}
    />
  );
}

const CustomizedDateField = ({
  form,
  field,
  disabled,
  autoComplete,
}: Omit<ComponentType, 'autoComplete'> & {
  autoComplete?: AutoCompleteType;
}) => {
  const { t } = useTranslation();
  const { name, value } = field;
  const { setFieldValue, setFieldTouched } = form;
  const values = value.toString().split('/');
  const [day, setDay] = useState<string>(values[0] || '');
  const [month, setMonth] = useState<string>(values[1] || '');
  const [year, setYear] = useState<string>(values[2] || '');
  const classes = classNames(fieldStyles.field, styles.field);

  function convertToNumber(value: string) {
    return value.replace(/\D/g, '');
  }

  function convertToValidNumber(value: string) {
    if (value.length === 1 && value !== '0') {
      return '0' + value;
    }
    return value;
  }

  function validateFieldYear() {
    if (year.length < 4) {
      setFieldTouched(name, true);
    }
  }

  useEffect(() => {
    const date = `${day}/${month}/${year}`;
    setFieldValue(name, day || month || year ? date : '');
  }, [day, month, year, value]);

  return (
    <div className={styles.fields}>
      <input
        type="text"
        className={classes}
        value={day}
        maxLength={2}
        disabled={disabled}
        placeholder={t('LABEL_DAY')}
        onChange={e => setDay(convertToNumber(e.target.value))}
        onBlur={e => setDay(convertToValidNumber(e.target.value))}
        autoComplete={autoComplete?.day}
      />
      <input
        type="text"
        className={classes}
        value={month}
        maxLength={2}
        disabled={disabled}
        placeholder={t('LABEL_MONTH')}
        onChange={e => setMonth(convertToNumber(e.target.value))}
        onBlur={e => setMonth(convertToValidNumber(e.target.value))}
        autoComplete={autoComplete?.month}
      />
      <input
        type="text"
        className={classes}
        value={year}
        minLength={4}
        disabled={disabled}
        maxLength={4}
        placeholder={t('LABEL_YEAR')}
        onChange={e => setYear(convertToNumber(e.target.value))}
        onBlur={validateFieldYear}
        autoComplete={autoComplete?.year}
      />
    </div>
  );
};

type AutoCompleteType = Record<'day' | 'month' | 'year', string>;
